/**
 * Development Status page - hidden, only accessible via URL /status.
 * Shows versions, health, frontend/backend env vars, Forge API status, and runtime info in tabs.
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import statusService, { type BackendStatus } from '../../services/statusService';
import { getSystemHealth } from '../../services/forgeDashboardService';
import type { ForgeSystemHealth } from '../../types';

type TabId = 'overview' | 'frontend-env' | 'backend-env' | 'forge-api' | 'runtime';

const FRONTEND_ENV_KEYS = [
  'VITE_BASE_URL',
  'VITE_API_BASE_URL',
  'VITE_LOGTO_ENDPOINT',
  'VITE_LOGTO_APP_ID',
  'VITE_LOGTO_CONSOLE_URL',
  'MODE',
  'DEV',
  'PROD',
  'SSR',
] as const;

function getFrontendEnvEntries(notSetLabel: string): { key: string; value: string }[] {
  const env = import.meta.env;
  return FRONTEND_ENV_KEYS.map((key) => {
    const val = env[key];
    return { key, value: val !== undefined && val !== '' ? String(val) : notSetLabel };
  });
}

export default function DevelopmentStatusPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [backendStatus, setBackendStatus] = useState<BackendStatus | null>(null);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [forgeHealth, setForgeHealth] = useState<ForgeSystemHealth | null>(null);
  const [forgeError, setForgeError] = useState<string | null>(null);
  const [forgeLoading, setForgeLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    statusService.getBackendStatus()
      .then((data) => {
        if (!cancelled) {
          setBackendStatus(data);
          setBackendError(null);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setBackendError(err.message || t('developmentStatus.backendStatusFailed'));
          setBackendStatus(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (activeTab !== 'forge-api') return;
    let cancelled = false;
    setForgeLoading(true);
    setForgeError(null);
    getSystemHealth()
      .then((data) => {
        if (!cancelled) {
          setForgeHealth(data);
          setForgeError(null);
        }
      })
      .catch((err: { response?: { status: number }; message?: string }) => {
        if (!cancelled) {
          setForgeHealth(null);
          const status = err.response?.status;
          if (status === 401 || status === 403) {
            setForgeError(t('developmentStatus.forgeAuthRequired'));
          } else {
            setForgeError(err.message || t('developmentStatus.forgeStatusFailed'));
          }
        }
      })
      .finally(() => {
        if (!cancelled) setForgeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: t('developmentStatus.overview') },
    { id: 'frontend-env', label: t('developmentStatus.frontendEnv') },
    { id: 'backend-env', label: t('developmentStatus.backendEnv') },
    { id: 'forge-api', label: t('developmentStatus.forgeApi') },
    { id: 'runtime', label: t('developmentStatus.runtime') },
  ];

  const backendLive = !loading && !backendError && backendStatus != null;
  const frontendLive = true;

  const frontendEnvEntries = getFrontendEnvEntries(t('developmentStatus.notSet'));
  const backendEnvEntries = backendStatus?.env
    ? Object.entries(backendStatus.env).map(([key, value]) => ({ key, value }))
    : [];

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-foreground">{t('layout.pages.developmentStatus', 'Development Status')}</h1>

      <div className="flex border-b border-border">
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-4">
          <section>
            <h2 className="text-lg font-medium mb-2">{t('developmentStatus.health')}</h2>
            <table className="w-full max-w-md border border-border rounded-lg overflow-hidden">
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="px-3 py-2 bg-muted/50 font-medium w-40">Frontend</td>
                  <td className="px-3 py-2">
                    <span className={frontendLive ? 'text-green-600 dark:text-green-400 font-medium' : 'text-muted-foreground'}>
                      {frontendLive ? t('developmentStatus.live') : '—'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 bg-muted/50 font-medium">Backend</td>
                  <td className="px-3 py-2">
                    {loading ? (
                      <span className="text-muted-foreground">…</span>
                    ) : (
                      <span className={backendLive ? 'text-green-600 dark:text-green-400 font-medium' : 'text-destructive font-medium'}>
                        {backendLive ? t('developmentStatus.live') : t('developmentStatus.unreachable')}
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
          <section>
            <h2 className="text-lg font-medium mb-2">{t('developmentStatus.versions')}</h2>
            <table className="w-full max-w-md border border-border rounded-lg overflow-hidden">
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="px-3 py-2 bg-muted/50 font-medium">Frontend</td>
                  <td className="px-3 py-2 font-mono text-sm">{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '—'}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 bg-muted/50 font-medium">Backend</td>
                  <td className="px-3 py-2 font-mono text-sm">{backendStatus?.version ?? (loading ? '…' : '—')}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 bg-muted/50 font-medium">Python</td>
                  <td className="px-3 py-2 font-mono text-sm text-muted-foreground">{backendStatus?.python_version ?? (loading ? '…' : '—')}</td>
                </tr>
              </tbody>
            </table>
          </section>
          <section>
            <h2 className="text-lg font-medium mb-2">{t('developmentStatus.environment')}</h2>
            <p className="text-sm text-muted-foreground">{backendStatus?.environment ?? (loading ? '…' : '—')}</p>
          </section>
        </div>
      )}

      {activeTab === 'frontend-env' && (
        <div>
          <h2 className="text-lg font-medium mb-2">{t('developmentStatus.frontendEnvTitle')}</h2>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50">
                  <th className="px-3 py-2 text-left text-sm font-medium">{t('developmentStatus.key')}</th>
                  <th className="px-3 py-2 text-left text-sm font-medium">{t('developmentStatus.value')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {frontendEnvEntries.map(({ key, value }) => (
                  <tr key={key}>
                    <td className="px-3 py-2 font-mono text-sm">{key}</td>
                    <td className="px-3 py-2 font-mono text-sm break-all">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'forge-api' && (
        <div>
          <h2 className="text-lg font-medium mb-2">{t('developmentStatus.forgeApiStatus')}</h2>
          {forgeLoading && <p className="text-muted-foreground text-sm mb-2">{t('common.loading')}</p>}
          {forgeError && (
            <p className="text-destructive text-sm mb-2">{forgeError}</p>
          )}
          {!forgeLoading && forgeHealth && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">{t('developmentStatus.overall')}</span>
                <span className={forgeHealth.status === 'success' ? 'text-green-600 dark:text-green-400' : 'text-destructive'}>
                  {forgeHealth.status}
                </span>
                {forgeHealth.message && <span className="text-muted-foreground">— {forgeHealth.message}</span>}
              </div>
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-3 py-2 text-left text-sm font-medium">{t('developmentStatus.component')}</th>
                      <th className="px-3 py-2 text-left text-sm font-medium">{t('global.status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(['backoffice', 'traefik', 'mongodb', 'argocd', 'tekton'] as const).map((key) => (
                      <tr key={key}>
                        <td className="px-3 py-2 font-medium capitalize">{key}</td>
                        <td className="px-3 py-2">
                          <span className={
                            forgeHealth.data[key]?.toLowerCase() === 'healthy'
                              ? 'text-green-600 dark:text-green-400'
                              : forgeHealth.data[key]?.toLowerCase() === 'unhealthy'
                                ? 'text-destructive'
                                : 'text-muted-foreground'
                          }>
                            {forgeHealth.data[key] ?? t('developmentStatus.unknown')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(forgeHealth.data.timestamp != null || forgeHealth.data.environment != null) && (
                <p className="text-xs text-muted-foreground">
                  {forgeHealth.data.timestamp && <>{t('developmentStatus.timestamp', { value: forgeHealth.data.timestamp })}</>}
                  {forgeHealth.data.timestamp && forgeHealth.data.environment && ' · '}
                  {forgeHealth.data.environment != null && <>{t('developmentStatus.environmentValue', { value: forgeHealth.data.environment })}</>}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'backend-env' && (
        <div>
          <h2 className="text-lg font-medium mb-2">{t('developmentStatus.backendEnvTitle')}</h2>
          {backendError && (
            <p className="text-destructive text-sm mb-2">{backendError}</p>
          )}
          {loading && !backendError && <p className="text-muted-foreground text-sm">{t('common.loading')}</p>}
          {!loading && backendStatus && (
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="px-3 py-2 text-left text-sm font-medium">{t('developmentStatus.key')}</th>
                    <th className="px-3 py-2 text-left text-sm font-medium">{t('developmentStatus.value')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {backendEnvEntries.map(({ key, value }) => (
                    <tr key={key}>
                      <td className="px-3 py-2 font-mono text-sm">{key}</td>
                      <td className="px-3 py-2 font-mono text-sm break-all">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'runtime' && (
        <div className="space-y-4">
          <section>
            <h2 className="text-lg font-medium mb-2">{t('developmentStatus.browserRuntime')}</h2>
            <table className="w-full max-w-2xl border border-border rounded-lg overflow-hidden">
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="px-3 py-2 bg-muted/50 font-medium w-40">{t('developmentStatus.userAgent')}</td>
                  <td className="px-3 py-2 font-mono text-sm break-all">{typeof navigator !== 'undefined' ? navigator.userAgent : '—'}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 bg-muted/50 font-medium">{t('developmentStatus.screen')}</td>
                  <td className="px-3 py-2 font-mono text-sm">
                    {typeof window !== 'undefined'
                      ? `${window.screen?.width ?? '—'} × ${window.screen?.height ?? '—'}`
                      : '—'}
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 bg-muted/50 font-medium">{t('developmentStatus.window')}</td>
                  <td className="px-3 py-2 font-mono text-sm">
                    {typeof window !== 'undefined'
                      ? `${window.innerWidth ?? '—'} × ${window.innerHeight ?? '—'}`
                      : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>
      )}
    </div>
  );
}
